/**
 * Shared utilities for campaign template variable handling.
 * Supports both numbered ({{1}}) and named ({{customer_name}}) parameter formats.
 */

function parseTemplateJson(template) {
  let json = template.template_json || {};
  if (typeof json === "string") {
    try {
      json = JSON.parse(json);
    } catch {
      json = {};
    }
  }
  return json;
}

/** Ordered unique named placeholders from template text ({{customer_name}}, …). */
function extractOrderedNamedVars(text) {
  if (!text) return [];
  const seen = new Set();
  const names = [];
  const re = /\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const name = m[1];
    if (!seen.has(name)) {
      seen.add(name);
      names.push(name);
    }
  }
  return names;
}

/** Ordered unique numbered placeholders from template text ({{1}}, {{2}}, …). */
function extractOrderedNumberedVars(text) {
  if (!text) return [];
  const seen = new Set();
  const nums = [];
  const re = /\{\{(\d+)\}\}/g;
  let m;
  while ((m = re.exec(text)) !== null) {
    const n = m[1];
    if (!seen.has(n)) {
      seen.add(n);
      nums.push(n);
    }
  }
  return nums;
}

/** Value is present — spaces and other whitespace count as valid content. */
export function hasTemplateVariableValue(value) {
  return value !== null && value !== undefined && String(value).length > 0;
}

/** True when a URL CTA button needs a runtime variable (dynamic URL). */
export function isDynamicUrlButton(btn) {
  if (!btn || String(btn.type || "").toUpperCase() !== "URL") return false;
  const url = btn.url || "";
  if (/\{\{[^}]+\}\}/.test(url)) return true;
  if (btn.url_type === "dynamic") return true;
  if (Array.isArray(btn.example) && btn.example.length > 0) return true;
  return false;
}

/**
 * Determine whether a template uses named or numbered parameters.
 */
export function getParameterFormat(template) {
  if (template.parameter_format === "named") return "named";
  const json = parseTemplateJson(template);
  if (json.parameter_format === "named") return "named";
  const components = json.components || [];
  const body = components.find((c) => c.type === "BODY");
  if (body?.example?.body_text_named_params) return "named";
  const header = components.find((c) => c.type === "HEADER");
  if (header?.example?.header_text_named_params) return "named";
  return "numbered";
}

/**
 * Identity columns the campaign CSV always needs.
 * Order matters: country_code first, then phone, so spreadsheets render
 * "+91" beside the local number left-to-right.
 */
export const IDENTITY_HEADERS = ["country_code", "phone"];

/**
 * Generate CSV headers for a template.
 * Named templates use param names (e.g. customer_name, order_id).
 * Numbered templates use body_1, body_2, header_1, etc.
 *
 * Identity columns (`country_code`, `phone`) lead the row so the user
 * has the recipient context first. They are kept separate so the
 * server can compose the WhatsApp wa_id (`country_code` + `phone`) at
 * campaign-creation time — broadcasting itself does no string surgery.
 */
export function generateCSVHeaders(template) {
  if (!template) return [...IDENTITY_HEADERS];

  const headers = [...IDENTITY_HEADERS];
  const json = parseTemplateJson(template);
  const components = json.components || [];
  const isNamed = getParameterFormat(template) === "named";

  // Header
  const headerComp = components.find((c) => c.type === "HEADER");
  if (headerComp) {
    const format = headerComp.format?.toUpperCase() || "NONE";
    if (format === "TEXT") {
      if (isNamed && headerComp.example?.header_text_named_params) {
        headerComp.example.header_text_named_params.forEach((p) =>
          headers.push(p.param_name)
        );
      } else {
        const headerText = headerComp.text || "";
        (headerText.match(/\{\{(\d+)\}\}/g) || []).forEach((m) => {
          headers.push(`header_${m.replace(/\{\{|\}\}/g, "")}`);
        });
      }
    }
  }

  // Body
  const bodyComp = components.find((c) => c.type === "BODY");
  if (bodyComp) {
    if (isNamed && bodyComp.example?.body_text_named_params) {
      bodyComp.example.body_text_named_params.forEach((p) =>
        headers.push(p.param_name)
      );
    } else {
      const bodyText = bodyComp.text || "";
      (bodyText.match(/\{\{(\d+)\}\}/g) || []).forEach((m) => {
        headers.push(`body_${m.replace(/\{\{|\}\}/g, "")}`);
      });
    }
  }

  // Buttons — dynamic URL segment per button index
  const buttonsComp = components.find((c) => c.type === "BUTTONS");
  (buttonsComp?.buttons || []).forEach((btn, index) => {
    if (isDynamicUrlButton(btn)) {
      headers.push(`button_text_${index}`);
    }
  });

  return headers;
}

/**
 * Validate that selected templates are compatible for a campaign.
 * Rules:
 *   1. All must share the same parameter format (named or numbered).
 *   2. All must require the same set of CSV columns.
 */
export function validateTemplateCompatibility(selectedTemplateObjects) {
  if (!selectedTemplateObjects || selectedTemplateObjects.length <= 1) {
    return { valid: true, error: "" };
  }

  const formats = selectedTemplateObjects.map((t) => getParameterFormat(t));
  const unique = [...new Set(formats)];

  if (unique.length > 1) {
    return {
      valid: false,
      error:
        "All templates must use the same parameter type. You have a mix of named and numbered templates. Please select only named or only numbered templates.",
    };
  }

  const parameterFormat = unique[0];

  const sigs = selectedTemplateObjects.map((t) => {
    const h = generateCSVHeaders(t);
    // Identity columns are pinned at the top in their canonical order;
    // remaining columns are alphabetised so naming-equivalent templates
    // produce identical signatures regardless of declaration order.
    const identity = IDENTITY_HEADERS.filter((k) => h.includes(k));
    const rest = h.filter((k) => !IDENTITY_HEADERS.includes(k)).sort((a, b) =>
      a.localeCompare(b)
    );
    const sorted = [...identity, ...rest];
    return {
      templateId: t.id,
      templateName: t.name,
      headers: sorted,
      headersString: sorted.join(", "),
    };
  });

  const ref = sigs[0].headers;
  const allMatch = sigs.every(
    (s) =>
      s.headers.length === ref.length &&
      s.headers.every((h, i) => h === ref[i])
  );

  if (!allMatch) {
    const details = sigs.map((s) => `"${s.templateName}": [${s.headersString}]`).join("; ");
    const msg =
      parameterFormat === "named"
        ? `Named templates must use the same variable names. Differences: ${details}`
        : `Templates have different CSV requirements: ${details}. All templates must require the same CSV columns.`;
    return { valid: false, error: msg };
  }

  return { valid: true, error: "" };
}

/**
 * Generate sample row value for a CSV header column.
 *
 * `country_code` and `phone` are kept as separate columns; the server
 * concatenates them when building the recipient wa_id, so users should
 * NOT pre-join them in their CSV. We provide the dial code without a
 * leading "+" because that's the form Meta's API accepts.
 */
export function sampleValueForHeader(header, rowIndex = 0) {
  if (header === "country_code") return "91";
  if (header === "phone") return `98765${String(43210 + rowIndex).padStart(5, "0")}`;
  // Legacy callers still passing "phone_number" — keep them working.
  if (header === "phone_number") return `+1234567890${rowIndex}`;
  if (header.startsWith("body_")) return `Sample Body Var ${header.split("_")[1]}`;
  if (header.startsWith("header_")) return `Sample Header Var ${header.split("_")[1]}`;
  if (header === "footer") return "Sample Footer";
  if (header.startsWith("button_text_")) return "Click Here";
  return `Sample ${header.replace(/_/g, " ")}`;
}

/**
 * Extract required template variables for forms (live chat, test send, etc.).
 * Returns { key, component, label, placeholder, description, varNum?, paramName? }.
 */
export function extractRequiredParameters(template) {
  if (!template) return [];

  const parameters = [];
  const parameterFormat = getParameterFormat(template);
  const templateJson = parseTemplateJson(template);
  const components = templateJson.components || [];

  const headerComponent = components.find((c) => c.type === "HEADER");
  if (headerComponent) {
    const headerFormat =
      headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || "NONE";
    const headerText = headerComponent.text || template.headerText || "";

    if (headerFormat === "TEXT") {
      const numberedFromText = extractOrderedNumberedVars(headerText);
      if (numberedFromText.length > 0) {
        numberedFromText.forEach((varNum) => {
          parameters.push({
            key: `header_${varNum}`,
            component: "HEADER",
            varNum,
            placeholder: `{{${varNum}}}`,
            label: `Header variable ${varNum}`,
            description: `Value for {{${varNum}}} in header`,
          });
        });
      } else if (parameterFormat === "named") {
        const namesFromText = extractOrderedNamedVars(headerText);
        if (namesFromText.length > 0) {
          namesFromText.forEach((paramName) => {
            parameters.push({
              key: paramName,
              component: "HEADER",
              paramName,
              placeholder: `{{${paramName}}}`,
              label: `Header variable: ${paramName}`,
              description: `Value for {{${paramName}}} in header`,
            });
          });
        } else if (headerComponent.example?.header_text_named_params) {
          headerComponent.example.header_text_named_params.forEach((param) => {
            const paramName = param?.param_name;
            if (!paramName) return;
            parameters.push({
              key: paramName,
              component: "HEADER",
              paramName,
              placeholder: `{{${paramName}}}`,
              label: `Header variable: ${paramName}`,
              description: `Value for {{${paramName}}} in header`,
            });
          });
        }
      } else {
        (headerText.match(/\{\{(\d+)\}\}/g) || []).forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, "");
          parameters.push({
            key: `header_${varNum}`,
            component: "HEADER",
            varNum,
            placeholder: match,
            label: `Header variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    }
  } else if (template.headerFormat || template.headerType) {
    const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
    const headerText = template.headerText || "";

    if (headerFormat === "TEXT") {
      (headerText.match(/\{\{(\d+)\}\}/g) || []).forEach((match) => {
        const varNum = match.replace(/\{\{|\}\}/g, "");
        parameters.push({
          key: `header_${varNum}`,
          component: "HEADER",
          varNum,
          placeholder: match,
          label: `Header variable ${varNum}`,
          description: `Value for ${match} in header`,
        });
      });
    }
  }

  const bodyComponent = components.find((c) => c.type === "BODY");
  const bodyText = bodyComponent?.text || template.body || "";
  const numberedFromText = extractOrderedNumberedVars(bodyText);

  if (numberedFromText.length > 0) {
    numberedFromText.forEach((varNum) => {
      parameters.push({
        key: `body_${varNum}`,
        component: "BODY",
        varNum,
        placeholder: `{{${varNum}}}`,
        label: `Body variable ${varNum}`,
        description: `Value for {{${varNum}}} in body text`,
      });
    });
  } else if (parameterFormat === "named") {
    const namesFromText = extractOrderedNamedVars(bodyText);
    if (namesFromText.length > 0) {
      namesFromText.forEach((paramName) => {
        parameters.push({
          key: paramName,
          component: "BODY",
          paramName,
          placeholder: `{{${paramName}}}`,
          label: `Body variable: ${paramName}`,
          description: `Value for {{${paramName}}} in body text`,
        });
      });
    } else if (bodyComponent?.example?.body_text_named_params) {
      bodyComponent.example.body_text_named_params.forEach((param) => {
        const paramName = param?.param_name;
        if (!paramName) return;
        parameters.push({
          key: paramName,
          component: "BODY",
          paramName,
          placeholder: `{{${paramName}}}`,
          label: `Body variable: ${paramName}`,
          description: `Value for {{${paramName}}} in body text`,
        });
      });
    }
  } else {
    (bodyText.match(/\{\{(\d+)\}\}/g) || []).forEach((match) => {
      const varNum = match.replace(/\{\{|\}\}/g, "");
      parameters.push({
        key: `body_${varNum}`,
        component: "BODY",
        varNum,
        placeholder: match,
        label: `Body variable ${varNum}`,
        description: `Value for ${match} in body text`,
      });
    });
  }

  const buttonsComponent = components.find((c) => c.type === "BUTTONS");
  const buttons = buttonsComponent?.buttons || template.buttons || [];
  buttons.forEach((btn, index) => {
    if (!isDynamicUrlButton(btn)) return;
    const url = btn.url || "";
    const placeholderMatch = url.match(/\{\{[^}]+\}\}/);
    parameters.push({
      key: `button_text_${index}`,
      component: "BUTTON",
      index,
      buttonText: btn.text || `Button ${index + 1}`,
      urlTemplate: url,
      placeholder: placeholderMatch ? placeholderMatch[0] : "Button URL variable",
      label: `URL button "${btn.text || `Button ${index + 1}`}" variable`,
      description: url
        ? `Value inserted into URL: ${url}`
        : `Dynamic URL value for button ${index + 1}`,
    });
  });

  return parameters;
}

/** Meta example/sample for a single extracted parameter. */
export function getSampleForParam(template, param) {
  if (!template || !param) return "";
  const templateJson = parseTemplateJson(template);
  const components = templateJson.components || [];

  if (param.component === "BODY") {
    const body = components.find((c) => c.type === "BODY");
    if (param.paramName) {
      const np = body?.example?.body_text_named_params || [];
      const found = np.find((p) => p?.param_name === param.paramName);
      return found?.example || "";
    }
    const idx = parseInt(param.varNum, 10) - 1;
    return body?.example?.body_text?.[0]?.[idx] || "";
  }

  if (param.component === "HEADER") {
    const header = components.find((c) => c.type === "HEADER");
    if (param.paramName) {
      const np = header?.example?.header_text_named_params || [];
      const found = np.find((p) => p?.param_name === param.paramName);
      return found?.example || "";
    }
    return header?.example?.header_text?.[0] || "";
  }

  if (param.component === "BUTTON") {
    const buttonsComp = components.find((c) => c.type === "BUTTONS");
    const btn = buttonsComp?.buttons?.[param.index];
    return btn?.example?.[0] || "Click here";
  }

  return "";
}

/** Map form values to numbered userVars (body_1, body_2, …) in template text order. */
export function buildUserVarsFromFormValues(template, formValues) {
  const userVars = {};
  let bodyPos = 0;
  let headerPos = 0;

  extractRequiredParameters(template).forEach((param) => {
    const value = formValues[param.key];
    let targetKey = param.key;

    if (param.component === "BODY") {
      bodyPos += 1;
      // Meta expects positional body_1, body_2, … in template text order
      targetKey = `body_${bodyPos}`;
    } else if (param.component === "HEADER") {
      headerPos += 1;
      targetKey = `header_${headerPos}`;
    }

    if (hasTemplateVariableValue(value)) {
      userVars[targetKey] = String(value);
    }
  });

  return userVars;
}

/** Validate that every required variable has a value (spaces count as valid). */
export function validateTemplateVariableValues(template, formValues) {
  const params = extractRequiredParameters(template);
  const errors = {};

  params.forEach((param) => {
    const val = formValues[param.key];
    if (!hasTemplateVariableValue(val)) {
      errors[param.key] = "This variable is required";
    }
  });

  return {
    valid: Object.keys(errors).length === 0,
    errors,
    params,
  };
}
